import React, { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import * as L from 'leaflet';
import { useTranslation } from 'react-i18next';
import { ccRequest } from '../../services/CCPublicationService';
import { authenticatedTiles } from './CCAuthenticatedTiles';
import { useYield } from './YieldContext';

export default function YieldOverlay() {
  const y = useYield(); const map = useMap(); const { t } = useTranslation('translation');
  const [status, setStatus] = useState(null); const [retry, setRetry] = useState(0);
  const current = useRef(y); current.current = y;
  const fitted = useRef(null);
  useEffect(() => {
    if (!y.queryKey || !y.yearInfo) return;
    const focus = JSON.stringify([y.dataset.id, y.year]);
    if (fitted.current !== focus) {
      const [w, s, e, n] = y.yearInfo.bounds;
      map.fitBounds([[s, w], [n, e]], { padding: [35, 70], maxZoom: 13, animate: false });
      fitted.current = focus;
    }
  }, [map, y.queryKey, y.dataset, y.year, y.yearInfo]);
  useEffect(() => {
    if (!y.queryKey) return;
    let alive = true; let controller;
    const key = y.queryKey; const params = current.current.params;
    setStatus(null);
    const error = failure => {
      if (!alive) return;
      if ([401, 403].includes(failure?.response?.status)) {
        current.current.select(null); controller?.abort(); map.removeLayer(tiles);
      }
      setStatus({ key, error: true });
    };
    const tiles = authenticatedTiles({ tile_path: coords => `yield/tiles/${coords.z}/${coords.x}/${coords.y}.png?${new URLSearchParams(params)}` }, error,
      raster => { if (alive) setStatus({ key, error: raster.errors.length > 0, loading: raster.pending > 0 }); }).addTo(map);
    const click = async e => {
      controller?.abort(); controller = new AbortController(); const { signal } = controller;
      current.current.select(null);
      try {
        const result = await ccRequest('yield/identify', { params: { ...params, lng: e.latlng.lng, lat: e.latlng.lat }, signal });
        if (alive && !signal.aborted && current.current.queryKey === key) {
          current.current.select(result.features[0] || null, key);
          setStatus({ key, empty: !result.features.length });
        }
      } catch (failure) { if (!signal.aborted) error(failure); }
    };
    map.on('click', click);
    return () => { alive = false; controller?.abort(); map.off('click', click); map.removeLayer(tiles); };
  }, [map, y.queryKey, retry]); // The query key owns immutable params and cancels stale work.
  useEffect(() => {
    if (!y.selected) return;
    const outline = L.geoJSON(y.selected, { interactive: false, style: { fillOpacity: 0, color: '#152d1d', weight: 3 } }).addTo(map);
    return () => map.removeLayer(outline);
  }, [map, y.selected]);
  if (status?.key !== y.queryKey) return null;
  return <div className="cc-map-status" role="status">
    {status.loading && t('yield.loading')}
    {status.empty && t('yield.noField')}
    {status.error && <>{t('yield.mapError')} <button onClick={e => { e.stopPropagation(); setRetry(n => n + 1); }}>{t('cc.retry')}</button></>}
  </div>;
}
